import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperclip, faTimes } from '@fortawesome/pro-solid-svg-icons';
import DropUploader from '../../ImageUpload/DropUploader';

const MESSAGE_HEADLINE_MAX = 60;
const MESSAGE_BODY_MAX = 500;
const MB_SIZE = 1024 * 1024;
const ATTACHMENT_MAX_SIZE = 10 * MB_SIZE;
const ATTACHMENT_WARNING_SIZE = 2 * MB_SIZE;

const MessageSection = ({
  messageHeadline,
  onMessageHeadlineChange,
  messageBody,
  onMessageBodyChange,
  attachmentUrl,
  onAttachmentUrlChange,
  onAttachmentFileChange
}) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadComplete = url => {
    onAttachmentUrlChange(url);
    setIsUploading(false);
  };

  const handleDrop = (accepted, rejected) => {
    if (rejected?.length) {
      const isTooLarge = rejected.some(rejection => rejection?.errors?.some(error => error?.code === 'file-too-large'));
      window.ALERT?.error?.(isTooLarge ? 'Attachment is too large. Please choose a file smaller than 10MB.' : 'Unsupported file type.');
      return;
    }
    const file = accepted?.[0];
    if (!file) {
      return;
    }
    if (file.size > ATTACHMENT_WARNING_SIZE) {
      window.ALERT?.warn?.('This attachment is larger than 2MB and may load slowly for recipients.');
    }
    onAttachmentFileChange(file);
    setIsUploading(true);
  };

  const handleRemoveAttachment = () => {
    onAttachmentUrlChange(null);
    onAttachmentFileChange(null);
  };

  return (
    <section className='announcements-section'>
      <h2 className='announcements-section-title'>Message</h2>
      <div className='announcements-field'>
        <label className='announcements-label'>
          Message Headline <span className='announcements-label-required'>*</span>
        </label>
        <input
          type='text'
          className='announcements-input'
          value={messageHeadline}
          onChange={e => onMessageHeadlineChange(e.target.value.slice(0, MESSAGE_HEADLINE_MAX))}
          placeholder='Enter message headline'
          maxLength={MESSAGE_HEADLINE_MAX}
        />
        <div className='announcements-char-count'>
          {messageHeadline.length}/{MESSAGE_HEADLINE_MAX}
        </div>
      </div>
      <div className='announcements-field'>
        <label className='announcements-label'>
          Message Body <span className='announcements-label-required'>*</span>
        </label>
        <textarea
          className='announcements-textarea'
          value={messageBody}
          onChange={e => onMessageBodyChange(e.target.value.slice(0, MESSAGE_BODY_MAX))}
          placeholder='Enter message body'
          rows={4}
          maxLength={MESSAGE_BODY_MAX}
        />
        <div className='announcements-char-count'>
          {messageBody.length}/{MESSAGE_BODY_MAX}
        </div>
      </div>
      <div className='announcements-field'>
        <DropUploader
          onUpload={handleUploadComplete}
          onDrop={handleDrop}
          allowClicks
          className='announcements-drop-uploader'
          dropMessage='Drop file to upload'
          isDisabled={isUploading}
          maxSize={ATTACHMENT_MAX_SIZE}
        >
          <button type='button' className='announcements-attachment-btn' disabled={isUploading}>
            <FontAwesomeIcon icon={faPaperclip} className='announcements-attachment-icon' />
            {isUploading ? 'Uploading...' : 'Upload Image'}
          </button>
        </DropUploader>
        {attachmentUrl && (
          <div className='announcements-attachment-preview'>
            <span className='announcements-attachment-url'>{attachmentUrl.split('/').pop()}</span>
            <button type='button' className='announcements-attachment-remove' onClick={handleRemoveAttachment} aria-label='Remove attachment'>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

MessageSection.propTypes = {
  messageHeadline: PropTypes.string.isRequired,
  onMessageHeadlineChange: PropTypes.func.isRequired,
  messageBody: PropTypes.string.isRequired,
  onMessageBodyChange: PropTypes.func.isRequired,
  attachmentUrl: PropTypes.string,
  onAttachmentUrlChange: PropTypes.func.isRequired,
  onAttachmentFileChange: PropTypes.func.isRequired
};

export default MessageSection;
